import { watchThrottled } from '@vueuse/core'

/**
 * Watch a reactive object and run a callback when it changes, using a snapshot
 * that excludes specific ignored keys.
 *
 * @param source - Getter that returns the object to watch
 * @param keysToIgnore - An array of keys to exclude from the change detection
 * @param callback - Called when the object's content (excluding ignored keys) changes
 * @param options - throttle: ms between callback runs (default 1000)
 */
export function watchThrottledIgnoringKeys<T extends object, K extends keyof T>(
	source: () => T | undefined | null,
	keysToIgnore: K[],
	callback: (value: T) => void | Promise<void>,
	options?: { throttle?: number }
): () => void {
	const throttle = options?.throttle ?? 1000

	return watchThrottled(
		() => {
			const watchedObject = source()
			if (!watchedObject) return undefined

			// Create a shallow copy and delete ignored keys
			const objCopy = { ...watchedObject } as Record<string, unknown>
			for (const key of keysToIgnore) {
				delete objCopy[key as string]
			}

			try {
				return JSON.stringify(objCopy)
			} catch {
				return undefined
			}
		},
		() => {
			const current = source()
			if (current) {
				callback(current)
			}
		},
		{ throttle }
	)
}
